import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { toast } from "sonner";
import { Plus, Play, CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { RULE_KINDS, KIND_META, configHasPlaceholder, type RuleKind } from "./kindConfig";
import { WatchRuleTarget } from "./WatchRuleTarget";
import { friendlyError } from "./friendlyError";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type Rule = {
  id: string;
  rule_kind: RuleKind;
  name: string;
  config: any;
  enabled: boolean;
  default_tags: any;
  default_visibility: "internal_only" | "partners";
  last_run_at: string | null;
  last_result: any;
  created_at: string;
};

export default function WatchRulesList() {
  const { data: canManage } = useResearchCapability("manage");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<RuleKind | "all">("all");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<Rule | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());

  const { data: rules, isLoading } = useQuery({
    queryKey: ["research_watch_rules"],
    queryFn: async () => {
      const { data, error } = await supabase.from("research_watch_rules").select("*").order("rule_kind").order("name");
      if (error) throw error;
      return data as Rule[];
    },
    refetchInterval: 15_000,
  });

  const filtered = useMemo(() => {
    let r = rules ?? [];
    if (kindFilter !== "all") r = r.filter((x) => x.rule_kind === kindFilter);
    if (enabledFilter !== "all") r = r.filter((x) => x.enabled === (enabledFilter === "on"));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.name.toLowerCase().includes(q) || JSON.stringify(x.config).toLowerCase().includes(q));
    }
    return r;
  }, [rules, kindFilter, enabledFilter, search]);

  const toggleEnabled = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase.from("research_watch_rules").update({ enabled }).eq("id", id);
      if (error) throw error;
    },
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: ["research_watch_rules"] });
      const prev = qc.getQueryData<Rule[]>(["research_watch_rules"]);
      qc.setQueryData<Rule[]>(["research_watch_rules"], (old) => old?.map((r) => r.id === id ? { ...r, enabled } : r) ?? []);
      return { prev };
    },
    onError: (e: any, _v, ctx) => { qc.setQueryData(["research_watch_rules"], ctx?.prev); toast.error(e.message); },
  });

  const bulkSet = async (enabled: boolean) => {
    if (selected.size === 0) return;
    const { error } = await supabase.from("research_watch_rules").update({ enabled }).in("id", Array.from(selected));
    if (error) toast.error(error.message);
    else { toast.success(`${selected.size} rule(s) ${enabled ? "enabled" : "disabled"}`); setSelected(new Set()); qc.invalidateQueries({ queryKey: ["research_watch_rules"] }); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const { error } = await supabase.from("research_watch_rules").delete().in("id", Array.from(selected));
    if (error) toast.error(error.message);
    else { toast.success(`${selected.size} rule(s) deleted`); setSelected(new Set()); qc.invalidateQueries({ queryKey: ["research_watch_rules"] }); }
  };

  const runNow = async (rule: Rule) => {
    const meta = KIND_META[rule.rule_kind];
    if (!meta.supportsRunNow) { toast.info("Run-now coming soon for this kind"); return; }
    setRunningIds((s) => new Set(s).add(rule.id));
    setTimeout(() => setRunningIds((s) => { const n = new Set(s); n.delete(rule.id); return n; }), 30_000);
    const { data, error } = await supabase.functions.invoke(meta.ingestFn, { body: { watch_rule_id: rule.id, dry_run: false } });
    // Try to extract a meaningful error from either the SDK error context or the response body
    let raw: string | null = null;
    if (error) {
      try {
        const ctx: any = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json();
          raw = body?.error ?? body?.message ?? JSON.stringify(body);
        } else {
          raw = error.message;
        }
      } catch { raw = error.message; }
    } else if (data && data.ok === false) {
      raw = data.error ?? data.last_result?.error ?? "Ingest returned ok:false";
    }
    if (raw) {
      const f = friendlyError(raw);
      toast.error(f.message, { description: f.hint });
    } else {
      toast.success(`Triggered ${meta.label}.`, {
        action: {
          label: "View runs",
          onClick: () => navigate(`/partner/research/watch-rules/${rule.id}/runs`),
        },
      });
    }
    qc.invalidateQueries({ queryKey: ["research_watch_rules"] });
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { error } = await supabase.from("research_watch_rules").delete().eq("id", confirmDelete.id);
    if (error) toast.error(error.message);
    else { toast.success("Rule deleted"); qc.invalidateQueries({ queryKey: ["research_watch_rules"] }); }
    setConfirmDelete(null);
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading watch rules…</p>;

  const empty = (rules?.length ?? 0) === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">Watch rules</h2>
          <p className="text-sm text-muted-foreground">{filtered.length} of {rules?.length ?? 0} rule(s)</p>
        </div>
        {canManage && (
          <Button asChild><Link to="/partner/research/watch-rules/new"><Plus className="h-4 w-4 mr-1" /> New rule</Link></Button>
        )}
      </div>

      {empty && (
        <Card><CardContent className="p-4 text-sm">
          No rules yet. The seeded defaults should appear here — if not, the seed migration hasn't run.
        </CardContent></Card>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setKindFilter("all")} className={pillClass(kindFilter === "all")}>All</button>
            {RULE_KINDS.map((k) => (
              <button key={k.kind} onClick={() => setKindFilter(k.kind)} className={pillClass(kindFilter === k.kind)}>
                <k.icon className="h-3 w-3 inline mr-1" />{k.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 text-xs">
              {(["all", "on", "off"] as const).map((v) => (
                <button key={v} onClick={() => setEnabledFilter(v)} className={pillClass(enabledFilter === v)}>
                  {v === "all" ? "All states" : v === "on" ? "Enabled" : "Disabled"}
                </button>
              ))}
            </div>
            <Input className="max-w-xs" placeholder="Search name or config…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {canManage && selected.size > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button size="sm" variant="outline" onClick={() => bulkSet(true)}>Enable ({selected.size})</Button>
                <Button size="sm" variant="outline" onClick={() => bulkSet(false)}>Disable</Button>
                <Button size="sm" variant="destructive" onClick={bulkDelete}>Delete</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {canManage && <TableHead className="w-8"></TableHead>}
                <TableHead>Name</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead>Last result</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const meta = KIND_META[r.rule_kind];
                const Icon = meta?.icon;
                const result = r.last_result;
                const hasPlaceholder = configHasPlaceholder(r.config);
                return (
                  <TableRow key={r.id}>
                    {canManage && (
                      <TableCell>
                        <Checkbox checked={selected.has(r.id)} onCheckedChange={(c) => {
                          const n = new Set(selected);
                          c ? n.add(r.id) : n.delete(r.id);
                          setSelected(n);
                        }} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Link to={`/partner/research/watch-rules/${r.id}`} className="font-medium hover:underline">{r.name}</Link>
                      {hasPlaceholder && (
                        <Badge variant="outline" className="ml-2 gap-1 text-amber-600 border-amber-300">
                          <AlertTriangle className="h-3 w-3" /> Needs config
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <WatchRuleTarget kind={r.rule_kind} name={r.name} config={r.config} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="gap-1">
                        {Icon && <Icon className="h-3 w-3" />} {meta?.label ?? r.rule_kind}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={r.enabled} disabled={!canManage}
                        onCheckedChange={(v) => toggleEnabled.mutate({ id: r.id, enabled: v })} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.last_run_at ? `${formatDistanceToNowStrict(new Date(r.last_run_at))} ago` : "Never"}
                    </TableCell>
                    <TableCell className="text-xs max-w-xs">
                      {result?.ok === false ? (() => {
                        const f = friendlyError(result?.error ?? result?.errors ?? "error");
                        return (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-destructive inline-flex items-start gap-1 cursor-help">
                                  <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                  <span className="line-clamp-2">{f.message}</span>
                                  <Info className="h-3 w-3 mt-0.5 shrink-0 opacity-60" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm space-y-1">
                                <p className="font-medium">{f.message}</p>
                                {f.hint && <p className="text-xs opacity-80">{f.hint}</p>}
                                {f.raw && <p className="text-[10px] font-mono opacity-60 break-all">{f.raw.slice(0, 400)}</p>}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })() : result?.sources_created != null ? (
                        <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {result.sources_created} sources</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{r.default_visibility}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {canManage && (
                        <Button size="sm" variant="ghost" disabled={runningIds.has(r.id) || !meta?.supportsRunNow}
                          onClick={() => runNow(r)} title={meta?.supportsRunNow ? "Run now" : "Run-now coming soon"}>
                          <Play className="h-3 w-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/partner/research/watch-rules/${r.id}/runs`)}>Runs</Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/partner/research/watch-rules/${r.id}`)}>Edit</Button>
                      {canManage && (
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(r)}>Delete</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Past ingested sources will be kept (their <code>watch_rule_id</code> becomes null). The rule itself and its future triggers will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function pillClass(active: boolean) {
  return `px-2 py-1 text-xs rounded-full border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`;
}
